// src/components/resources/ebooks/EbookViewer.tsx

'use client'

import { useEffect, useMemo, type ReactNode } from 'react'
import { isEbook, type Ebook } from '~/lib/resources/types/ebooks'
import { clientLogger } from '~/lib/logger-client'
import { GatedContentEmailCapture } from '~/components/forms/variants/gated/GatedContentEmailCapture'
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog'
import { cn } from '~/lib/utils'
import {
  buildEbookGatedEmailModel,
  ebookPreviewImageUrl,
} from '~/lib/resources/data/ebooks/email-model'

interface EbookViewerProps {
  ebook: Ebook
  className?: string
  showEmailCapture?: boolean
  onCloseEmailCapture?: () => void
  accessGranted: boolean
  onEmailSuccess?: () => void
}

/**
 * Handles gated PDF download email capture for ebook detail pages.
 * Static landing content is rendered by `EbookViewerWrapper`.
 */
export function EbookViewer({
  ebook,
  className,
  showEmailCapture = false,
  onCloseEmailCapture,
  accessGranted,
  onEmailSuccess,
}: EbookViewerProps): ReactNode {
  const downloadConfig = ebook.config?.download
  const emailTemplate = useMemo(() => {
    if (!downloadConfig) return null
    const emailConfig = ebook.config?.access?.defaultGating?.requirements?.emailCapture
    if (!emailConfig?.template) return null

    const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL ?? 'https://www.adsights.ai'

    return {
      templateAlias: 'gated-resource-1',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      // Built via a pure, unit-tested helper so the email's only visual
      // (resource_icon_url) stays the per-title images.og, not the hub hero.
      templateModel: buildEbookGatedEmailModel(ebook, BASE_URL),
    }
  }, [ebook, downloadConfig])

  // Log the invalid-prop invariant from an effect (once per occurrence), never
  // from the render body — a render-path log re-POSTs to /api/log every re-render.
  useEffect(() => {
    if (!isEbook(ebook)) {
      clientLogger.error('Invalid ebook provided to EbookViewer', { component: 'EbookViewer' })
    }
  }, [ebook])

  if (!isEbook(ebook)) {
    return null
  }

  const isGated = (downloadConfig?.isGated ?? false) && Boolean(emailTemplate)

  return (
    <div className={cn('w-full', className)}>
      {isGated && emailTemplate && (
        <Dialog
          open={showEmailCapture}
          onOpenChange={(open) => {
            if (!open) onCloseEmailCapture?.()
          }}
        >
          <DialogContent className="max-w-2xl gap-0 border-0 bg-transparent p-0 shadow-none sm:max-w-2xl">
            <DialogTitle className="sr-only">Get {ebook.title}</DialogTitle>
            <GatedContentEmailCapture
              resourceDetails={{
                type: 'PDF eBook',
                title: ebook.title,
                description: ebook.description,
                format: 'PDF',
                previewImage: ebookPreviewImageUrl(ebook),
              }}
              content={{
                title: { text: `Get ${ebook.title}`, className: 'text-2xl font-semibold' },
                description: {
                  text: 'Enter your email and we will send the eBook download link straight to your inbox.',
                  className: 'text-gray-600 dark:text-gray-400',
                },
                button: { text: 'Email me the eBook', loadingText: 'Sending…' },
                messages: {
                  success: 'Thanks! Check your inbox — we have emailed you the download link.',
                  error: 'Please enter a valid email address.',
                },
              }}
              template={emailTemplate}
              validation={{
                rules: { required: true, format: true, requireCorporateEmail: false },
                messages: { required: 'Please enter your email address', corporate: 'Please use your work email' },
              }}
              analytics={{
                enabled: true,
                category: 'ebooks',
                action: 'email_capture',
                label: `ebook_${ebook.id}`,
                elementId: `ebook-email-form-${ebook.id}`,
              }}
              crm={{
                tags: ['ebook_access'],
                customFields: {
                  ebook_id: ebook.id,
                  ebook_name: ebook.title,
                  ebook_category: ebook.category,
                },
              }}
              onSuccess={onEmailSuccess}
            />
          </DialogContent>
        </Dialog>
      )}

      {accessGranted && downloadConfig && (
        <p className="sr-only" aria-live="polite">
          Download access granted for {ebook.title}
        </p>
      )}
    </div>
  )
}
